import React, { useState, useEffect } from "react";

import {
  Grid,
  makeStyles,
  Theme,
  styled,
  createStyles,
  Button,
  Modal,
  Fab,
  TextField,
} from "@material-ui/core";
import Snackbar from "../snackbar";
import Content from "../content";
import { IStyledArguments } from "../../types/styled-arguments";

import { injectIntl } from "react-intl";
import messages from "./PageViewPlayer.intl";
import { ConfirmDialog } from "../atoms";
import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import CopyIcon from "@material-ui/icons/FileCopy";
import useIsAdmin from "../../hooks/useIsAdmin";
import { Redirect } from "@reach/router";
import CancelIcon from "@material-ui/icons/CancelOutlined";
import HistoricalCard from "./HistoricalCard";
import ContactDetailsCard from "./ContactDetailsCard";
import TransferPlayerDialog from "./TransferPlayerDialog";

const DELETE_PLAYER_SCOUTING = gql`
  mutation DeletePlayerScouting($id: ID!) {
    deletePlayerScouting(id: $id) {
      op
      error
    }
  }
`;

const SEND_INVITE = gql`
  mutation InvitePlayer($id: ID!, $email: String!, $role: String!) {
    invitePlayer(id: $id, email: $email, role: $role)
  }
`;

const TRANSFER_PLAYER = gql`
  mutation TransferPlayer($id: ID!, $teamID: ID!) {
    transferPlayer(id: $id, teamID: $teamID) {
      op
      error
    }
  }
`;

const UNLINK_USER = gql`
  mutation UnLinkUser($id: ID!) {
    unLinkUser(id: $id) {
      op
      error
    }
  }
`;
const REMOVE_PLAYER = gql`
  mutation RemovePlayer($id: ID!) {
    removePlayer(id: $id) {
      op
      error
    }
  }
`;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      position: "absolute",
      maxWidth: "90vw",
      backgroundColor: theme.palette.background.paper,
      border: "2px solid #000",
      boxShadow: theme.shadows[5],
      padding: theme.spacing(2, 4, 3),
    },
    textField: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      width: 200,
    },
  })
);

const Main = styled("main")(({ theme }: IStyledArguments) => ({
  display: "block",
  minHeight: `calc(100vh - ${theme.layout.headerHeight + 49}px)`,
  background: "transparent",
}));

const MainContent = styled(Content)(({ theme }: IStyledArguments) => ({
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const GridContainer = styled((props) => <Grid container {...props} direction="row" />)({
  flex: 1,
});

/* -------------------------------------------------------------------- */
/* Hero: goals as the headline number, games secondary, skills snapshot */
/* -------------------------------------------------------------------- */

const HeroCard = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: 20,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  padding: theme.spacing(2.75),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2.25),
  },
}));

const HeroTop = styled("div")({
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "space-between",
});

const HeroLabel = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: 0.8,
  textTransform: "uppercase",
  color: theme.palette.extra.win,
}));

const HeroNumber = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 800,
  fontSize: "3rem",
  lineHeight: 1,
  color: theme.palette.text.primary,
  fontVariantNumeric: "tabular-nums",
  marginTop: 4,
}));

const SecondaryStat = styled("div")({
  textAlign: "right",
});

const SecondaryStatValue = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 800,
  fontSize: "1.15rem",
  color: theme.palette.text.primary,
}));

const SecondaryStatLabel = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.62rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  color: theme.palette.text.secondary,
}));

const Divider = styled("div")(({ theme }: IStyledArguments) => ({
  borderTop: `1px solid ${theme.palette.extra.borderColor}`,
  margin: theme.spacing(2.25, 0, 1.75),
}));

const SkillsTitle = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 600,
  fontSize: "0.75rem",
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1.25),
}));

const SkillRow = styled("div")({
  marginBottom: 8,
});

const SkillLabelRow = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 3,
});

const SkillTrack = styled("div")(({ theme }: IStyledArguments) => ({
  height: 6,
  borderRadius: 4,
  background: theme.palette.extra.backgroundGrey,
  overflow: "hidden",
}));

const SkillFill = styled("div")<{ $value: number }>(
  ({ theme, $value }: IStyledArguments & { $value: number }) => ({
    width: `${Math.max(0, Math.min(100, $value * 10))}%`,
    height: "100%",
    background: theme.palette.primary.main,
    borderRadius: 4,
  })
);

const InviteButton = styled(Button)(({ theme }: IStyledArguments) => ({
  width: "100%",
  borderRadius: 10,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  color: theme.palette.primary.dark,
  padding: theme.spacing(1.5, 0),
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 600,
}));

/* -------------------------------------------------------------------- */
/* Manage: settings-style rows instead of stacked buttons / accordions  */
/* -------------------------------------------------------------------- */

const ManageCard = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: 16,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  overflow: "hidden",
}));

const ManageRow = styled("button")<{ $danger?: boolean }>(
  ({ theme, $danger }: IStyledArguments & { $danger?: boolean }) => ({
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "transparent",
    border: "none",
    borderBottom: `1px solid ${theme.palette.extra.borderColor}`,
    padding: theme.spacing(1.9, 2.25),
    cursor: "pointer",
    appearance: "none",
    fontFamily: theme.typography.fontFamily,
    "&:last-child": {
      borderBottom: "none",
    },
    "& .manage-row-label": {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      fontSize: "0.9rem",
      fontWeight: 500,
      color: $danger ? theme.palette.extra.loss : theme.palette.text.primary,
    },
    "& svg": {
      color: $danger ? theme.palette.extra.loss : theme.palette.text.secondary,
    },
  })
);

const ManageRowDisabled = styled(ManageRow)({
  opacity: 0.55,
  cursor: "default",
});

const SoonBadge = styled("span")(({ theme }: IStyledArguments) => ({
  fontSize: "0.6rem",
  fontWeight: 700,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  background: theme.palette.extra.backgroundGrey,
  borderRadius: 6,
  padding: "2px 6px",
}));

function getModalStyle() {
  const top = 50;
  const left = 50;

  return {
    top: `${top}%`,
    left: `${left}%`,
    transform: `translate(-${top}%, -${left}%)`,
  };
}

const skillKeys = ["speed", "shoot", "dribbling"] as const;

const InfoTab = injectIntl<any>(({ player, historicalData, intl, callback }) => {
  const classes = useStyles();

  const [modalStyle] = useState(getModalStyle);
  const [open, setOpen] = useState<boolean>(false);
  const [inviteEmail, setInviteEmail] = useState<string>("");
  const [invitationURL, setInvitationURL] = useState<any>();
  const [textArea, setTextArea] = useState<any>();
  const [copySuccess, setCopySuccess] = useState<any>();
  const [openRedirect, setOpenRedirect] = useState<boolean>(false);
  const [openError, setOpenError] = useState<boolean>(false);
  const [transferOpen, setTransferOpen] = useState<boolean>(false);
  const [sendInvite, { data: sendInviteData }] = useMutation(SEND_INVITE);
  const [deletePlayerScouting, { data: deletedData }] = useMutation(DELETE_PLAYER_SCOUTING);
  const [transferPlayer, { data: transferPlayerData }] = useMutation(TRANSFER_PLAYER);
  const [removePlayer, { data: removePlayerData }] = useMutation(REMOVE_PLAYER);
  const [unlinkUser] = useMutation(UNLINK_USER);

  useEffect(() => {
    if (sendInviteData) {
      setInvitationURL(sendInviteData.invitePlayer);
    }
  }, [sendInviteData]);

  useEffect(() => {
    if (transferPlayerData) {
      callback();
    }
  }, [transferPlayerData]);

  useEffect(() => {
    if (deletedData && deletedData.deletePlayerScouting) {
      if (deletedData.deletePlayerScouting.op === "ok") {
        setOpenRedirect(true);
      } else {
        setOpenError(true);
      }
    }
  }, [deletedData]);

  useEffect(() => {
    if (removePlayerData) {
      if (removePlayerData.removePlayer.op === "ok") {
        setOpenRedirect(true);
      } else {
        setOpenError(true);
      }
    }
  }, [removePlayerData]);

  if (openRedirect) {
    return <Redirect to="/people/players?userDeleted=true" noThrow />;
  }

  const handleDeletePlayer = async () => {
    await deletePlayerScouting({
      variables: {
        id: player.id,
      },
    });
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const copyToClipboard = (e: any) => {
    textArea.select();
    document.execCommand("copy");
    e.target.focus();
    setCopySuccess("Copied!");
  };

  const handleSetInviteEmail = (event: any) => {
    setInviteEmail(event.target.value);
  };

  const handleSendInvite = async () => {
    await sendInvite({
      variables: {
        id: player.id,
        email: inviteEmail,
        role: "PLAYER",
      },
    });
  };
  const handleTransfer = async (newTeamID: string) => {
    setTransferOpen(false);
    await transferPlayer({
      variables: {
        id: player.id,
        teamID: newTeamID,
      },
    });
  };

  const handleRemovePlayer = async () => {
    await removePlayer({
      variables: {
        id: player.id,
      },
    });
  };
  const handleUnlinkUser = async () => {
    await unlinkUser({
      variables: {
        id: player.id,
      },
    });
    callback();
  };

  const lastReview = player.reviews && player.reviews.length > 0 ? player.reviews[player.reviews.length - 1] : null;
  const isAdmin = useIsAdmin();
  const canInvite =
    isAdmin && player.isInvited === false && !player.user && !player.scouting && player.team !== null;
  const canDelete = isAdmin && (player.scouting || player.team === null);

  return (
    <Main>
      <Modal aria-labelledby="simple-modal-title" aria-describedby="simple-modal-description" open={open} onClose={handleClose}>
        <div style={modalStyle} className={classes.paper}>
          <h2 id="simple-modal-title">
            Invite {player.name} {player.surname}
          </h2>

          <p id="simple-modal-description">Introduce the email of the player</p>
          <TextField
            id="outlined-basic"
            className={classes.textField}
            label="email"
            margin="normal"
            variant="outlined"
            onChange={(e) => handleSetInviteEmail(e)}
            value={inviteEmail}
          />
          {invitationURL && (
            <div>
              <form>
                <textarea
                  style={{ width: "200px", height: "80px" }}
                  ref={(textarea) => setTextArea(textarea)}
                  value={invitationURL}
                />
              </form>
              {document.queryCommandSupported("copy") && (
                <>
                  <p id="simple-modal-description">
                    An email have been sent. But you can copy the invitation and share it by Whatsapp or text
                    message.
                  </p>
                  <div>
                    <Fab size="small" color="inherit" aria-label="add" onClick={copyToClipboard}>
                      <CopyIcon />
                    </Fab>
                    {copySuccess}
                  </div>
                </>
              )}
            </div>
          )}

          <GridContainer>
            <Grid item xs={12}>
              <Button variant="contained" color="primary" onClick={handleSendInvite} disabled={invitationURL}>
                Send
              </Button>
              <Button style={{ marginLeft: 16 }} variant="contained" color="secondary" onClick={handleClose}>
                close
              </Button>
            </Grid>
          </GridContainer>
        </div>
      </Modal>

      <TransferPlayerDialog
        currentTeam={player.team && player.team.name}
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        callback={(x: any) => handleTransfer(x)}
      />

      <MainContent container alignItems="stretch" justify="flex-start">
        <HeroCard>
          <HeroTop>
            <div>
              <HeroLabel>{intl.formatMessage(messages.goals)}</HeroLabel>
              <HeroNumber>{player.totalGoals || 0}</HeroNumber>
            </div>
            <SecondaryStat>
              <SecondaryStatValue>{player.totalMatches || 0}</SecondaryStatValue>
              <SecondaryStatLabel>{intl.formatMessage(messages.games)}</SecondaryStatLabel>
            </SecondaryStat>
          </HeroTop>

          {lastReview && (
            <>
              <Divider />
              <SkillsTitle>{intl.formatMessage(messages.newPlayerReview)}</SkillsTitle>
              {skillKeys.map((key) => (
                <SkillRow key={key}>
                  <SkillLabelRow>
                    <span style={{ fontSize: "0.75rem", color: "#5B6B67", fontWeight: 500 }}>
                      {intl.formatMessage((messages as any)[key])}
                    </span>
                    <span style={{ fontSize: "0.75rem", color: "#10151A", fontWeight: 700 }}>
                      {lastReview.data[key]}
                    </span>
                  </SkillLabelRow>
                  <SkillTrack>
                    <SkillFill $value={lastReview.data[key]} />
                  </SkillTrack>
                </SkillRow>
              ))}
            </>
          )}
        </HeroCard>

        {canInvite && (
          <InviteButton variant="outlined" onClick={handleOpen}>
            {intl.formatMessage(messages.invite)}
          </InviteButton>
        )}

        {player.user && <ContactDetailsCard user={player.user} />}

        {historicalData && <HistoricalCard data={historicalData} />}

        {isAdmin && (
          <ManageCard>
            <ManageRow type="button" onClick={() => setTransferOpen(true)}>
              <span className="manage-row-label">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M7 7h10M7 12h10M7 17h6" />
                </svg>
                {intl.formatMessage(messages.transferPlayer)}
              </span>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <polyline points="9 6 15 12 9 18" />
              </svg>
            </ManageRow>

            {player.user && (
              <ManageRow type="button" onClick={handleUnlinkUser}>
                <span className="manage-row-label">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path d="M18.36 5.64a9 9 0 11-12.73 0" />
                    <path d="M12 2v10" />
                  </svg>
                  {intl.formatMessage(messages.removeAssociatedUser)}
                </span>
              </ManageRow>
            )}

            {player.isInvited && (
              <ManageRow type="button" onClick={handleOpen}>
                <span className="manage-row-label">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path d="M21 12a9 9 0 11-3.5-7.1M21 4v6h-6" />
                  </svg>
                  {intl.formatMessage(messages.resendInvitation)}
                </span>
              </ManageRow>
            )}

            <ManageRowDisabled type="button" disabled>
              <span className="manage-row-label" style={{ opacity: 0.7 }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <rect x="4" y="4" width="16" height="16" rx="3" />
                </svg>
                Technical details
              </span>
              <SoonBadge>Soon</SoonBadge>
            </ManageRowDisabled>

            {canDelete && (
              <ConfirmDialog
                title={intl.formatMessage(messages.ConfirmDialogTitle)}
                message={intl.formatMessage(messages.ConfirmDialogMessage)}
                trigger={({ onOpen }: any) => (
                  <ManageRow type="button" $danger onClick={onOpen}>
                    <span className="manage-row-label">
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6" />
                      </svg>
                      {intl.formatMessage(messages.removePlayer)}
                    </span>
                  </ManageRow>
                )}
                callback={(response: any) => {
                  if (response === true) {
                    handleDeletePlayer();
                  }
                }}
              />
            )}

            {!canDelete && (
              <ManageRow type="button" $danger onClick={handleRemovePlayer} disabled={player.user}>
                <span className="manage-row-label">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6" />
                  </svg>
                  {intl.formatMessage(messages.removePlayer)}
                </span>
              </ManageRow>
            )}
          </ManageCard>
        )}

      </MainContent>

      <Snackbar
        open={openError}
        message={intl.formatMessage(messages.messageOnDelete)}
        variant="error"
        onClose={handleClose}
        action={() => {
          setOpenError(false);
        }}
        actionText={<CancelIcon />}
      />
    </Main>
  );
});

export default InfoTab;
